/* eslint-disable import/prefer-default-export */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import Skeleton from 'Common/Components/Skeleton';
import { enqueueCommand } from 'Common/Components/FileUploadContext/actions';
import CVEActionMenu from 'Common/Components/CVEActionMenu';
import {
  extractCommandIdFromResponse,
  hasCveListChanged,
  isWorkspaceGroupedByField,
  shouldTreatFieldUpdateAsGrouping
} from 'Common/Functions/vulnerabilityGrouping';
import { extractIsGroupingFromResponse } from 'Common/Functions/vulnerabilityNameEdit';
import { FEATURE_VULNS, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import {
  selectCurrentWorkspace,
  selectIsReadOnly,
  selectWorkspaces
} from 'store/Faraday/selectors';
import { updateVuln } from 'store/Manage/actions';
import { updateVulnGeneral } from 'store/ManageGeneral/actions';
import { updateVulnFromAsset } from 'store/Contextualization/AssetDetail/actions';
import { Input as CVEInput } from 'Screens/Contextualization/Manage/components/VulnerabilityDetail/components/Tabs/components/GeneralTab/components/CVE';
import EditVulnerabilityCVEModal from 'Screens/Contextualization/Manage/components/VulnerabilityDetail/components/Tabs/components/GeneralTab/components/CVE/components/EditVulnerabilityCVEModal';
import {
  Wrapper,
  ItemWrapper,
  EmptyAction
} from './styled';

const UPDATE_ACTIONS = {
  vulns: (vuln, field, value, options) => updateVuln(vuln, field, value, false, options),
  vulnsGeneral: (vuln, field, value, options) => updateVulnGeneral(vuln, field, value, options),
  vulnsAssets: (vuln, field, value, options) => updateVulnFromAsset(vuln, field, value, options)
};

const EMPTY_CVES = [];

export const CVE = ({ vuln, showSkeleton, entity }) => {
  const dispatch = useDispatch();
  const allowedByRole = useSelector((state) => selectAllowedByRole(state, FEATURE_VULNS, PERMISSION_TYPES.UPDATE));
  const readOnly = useSelector(selectIsReadOnly);
  const workspaces = useSelector(selectWorkspaces);
  const currentWorkspace = useSelector(selectCurrentWorkspace);
  const canUpdate = !readOnly && allowedByRole;
  const cves = get(vuln, 'cve', EMPTY_CVES);
  const workspaceName = get(vuln, 'workspace_name', currentWorkspace);
  const isGroupedByCve = isWorkspaceGroupedByField(workspaceName, workspaces, 'cve');
  const [list, setList] = useState(cves);
  const [showEditModal, setShowEditModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('Edit Vulnerability CVE');
  const [pendingUpdates, setPendingUpdates] = useState(0);
  const commandIdRef = useRef(null);
  const isGroupingRef = useRef(false);
  const groupingStartTimestampRef = useRef(null);
  const initialListRef = useRef([]);
  const updateAction = UPDATE_ACTIONS[entity];

  useEffect(() => {
    setList(cves);
  }, [cves, vuln?._id]);

  const persistCveList = useCallback(async (nextList) => {
    if (!updateAction) return { ok: false };

    const previousList = list;
    const groupingStartTimestamp = Date.now();
    setList(nextList);
    setPendingUpdates((value) => value + 1);

    try {
      const result = await dispatch(updateAction(vuln, 'cve', nextList, { enqueueGroupingFromResponse: false }));
      if (result?.ok === false) {
        setList(previousList);
        return result;
      }

      const commandId = result?.commandId || extractCommandIdFromResponse(result?.response);
      const isGrouping = shouldTreatFieldUpdateAsGrouping({
        workspaceName,
        workspaces,
        field: 'cve',
        commandId,
        isGrouping: result?.isGrouping ?? (result?.response ? extractIsGroupingFromResponse(result.response) === true : false)
      });
      commandIdRef.current = commandId ?? null;
      isGroupingRef.current = isGrouping === true;
      groupingStartTimestampRef.current = isGrouping === true ? groupingStartTimestamp : null;

      if (isGroupedByCve && hasCveListChanged(previousList, nextList) && isGrouping === true) {
        dispatch(enqueueCommand({
          command_id: commandId ?? null,
          commandType: 'grouping',
          name: workspaceName,
          fileType: 'grouping',
          isGrouping: true,
          groupingStartTimestamp,
          error: false,
          finished: false
        }));
      }

      return result;
    } finally {
      setPendingUpdates((value) => Math.max(0, value - 1));
    }
  }, [dispatch, isGroupedByCve, list, updateAction, vuln, workspaceName, workspaces]);

  const onAdd = useCallback((name) => {
    const nextList = [...list, name];
    return persistCveList(nextList);
  }, [list, persistCveList]);

  const onRemove = useCallback((name) => {
    const nextList = list.filter((item) => item !== name);
    return persistCveList(nextList);
  }, [list, persistCveList]);

  const handleOpenModal = useCallback(() => {
    if (!canUpdate) return;
    setModalTitle(list.length === 0 ? 'Add Vulnerability CVE' : 'Edit Vulnerability CVE');
    initialListRef.current = list;
    commandIdRef.current = null;
    isGroupingRef.current = false;
    groupingStartTimestampRef.current = null;
    setShowEditModal(true);
  }, [canUpdate, list]);

  const handleCloseModal = useCallback(() => {
    setShowEditModal(false);
    commandIdRef.current = null;
    isGroupingRef.current = false;
    groupingStartTimestampRef.current = null;
  }, []);

  const handleSaveModal = useCallback(() => {
    handleCloseModal();
  }, [handleCloseModal]);

  if (showSkeleton) return <Skeleton />;

  return (
    <>
      <Wrapper>
        {list.length === 0 && canUpdate && (
          <EmptyAction type="button" onClick={ handleOpenModal }>Add</EmptyAction>
        )}
        {list.map((cve, index) => (
          <ItemWrapper key={ `${cve}_${index}` }>
            <CVEActionMenu
              cve={ cve }
              onEdit={ handleOpenModal }
              canEdit={ canUpdate }
              variant="table"
            />
          </ItemWrapper>
        ))}
      </Wrapper>
      {canUpdate && (
        <EditVulnerabilityCVEModal
          show={ showEditModal }
          title={ modalTitle }
          onClose={ handleCloseModal }
          onSave={ handleSaveModal }
          showGroupingWarning={ isGroupedByCve }
          saveDisabled={ pendingUpdates > 0 }
          editor={ (
            <CVEInput
              placeholder="CVE-YYYY-NNNN"
              errorMessage="CVE not valid"
              validator={ /^(CVE-(1999|2\d{3})-(\d{4,7}))$/ }
              canUpdate
              onAdd={ onAdd }
              onRemove={ onRemove }
              listData={ list }
              useActionMenu
            />
          ) }
        />
      )}
    </>
  );
};

CVE.propTypes = {
  vuln: PropTypes.oneOfType([PropTypes.object, PropTypes.number]).isRequired,
  showSkeleton: PropTypes.bool.isRequired,
  entity: PropTypes.oneOf(['vulns', 'vulnsGeneral', 'vulnsAssets'])
};

CVE.defaultProps = {
  entity: 'vulns'
};
